import { useLocation, useNavigate } from 'react-router';

import { useAppSelector } from 'dux/app/hooks';

import findIndex from 'lodash/fp/findIndex';

import { Button, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import * as Sentry from '@sentry/nextjs';

import { Transition as TransitionComponent } from 'Apps/Consultation/constants/consultationComponents';
import { progressTrackerCategoriesList } from 'Apps/Consultation/constants/progressCategories';
import { useConsultationStepIsStandalone } from 'Apps/Consultation/hooks/useConsultationStepIsStandalone';
import { useConsultationSubsetCategory } from 'Apps/Consultation/hooks/useConsultationSubsetCategory';

import { productsCategories } from 'constants/products';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { getIsAuthenticated } from 'dux/auth/selectors';
import {
  getConsultationQuestionForIndex,
  getCurrentlyLoadedQuestionSet,
} from 'dux/consultation/selectors';
import * as userSelectors from 'dux/user/selectors';
import type { Question } from 'dux/feedback/types';

const ProgressTrackerContainer = styled.div`
  position: fixed;
  top: ${theme.env.headerHeight};
  left: 0;
  right: 0;
  height: 30px;
  display: flex;
  flex-direction: row;
  /* Originally at 1100 but for the first-trial-offer-promo-ab-test I need to put it to this value */
  z-index: 1098;
  justify-content: stretch;
  background-color: ${theme.colors.neutral[600]};
`;

const CategoryButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isClickable',
})<{ isClickable: boolean }>`
  cursor: ${(props) => (props.isClickable ? 'pointer' : '')};
  border: none;
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;

  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.neutral[200]};
  }
`;

const Advancement = styled.span`
  position: absolute;
  display: block;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: ${theme.colors.secondary[200]};
`;

type Props = {
  questionIndex: number;
  consultationCategory: typeof productsCategories.HAIRCARE | typeof productsCategories.SKINCARE;
};

export const ProgressTracker = ({ consultationCategory, questionIndex }: Props) => {
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const isStandalone = useConsultationStepIsStandalone();
  const navigate = useNavigate();
  const { isMobile } = useResponsiveVariant();
  const { search } = useLocation();
  const routeParams = new URLSearchParams(search);
  const consultationSubsetCategory = useConsultationSubsetCategory();

  const question: Question = useAppSelector((state) =>
    getConsultationQuestionForIndex(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  );
  const questions: Question[] = useAppSelector((state) =>
    getCurrentlyLoadedQuestionSet(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  );
  const hasCompletedHaircareConsultation = useAppSelector(
    userSelectors.getHasCompletedHaircareConsultation,
  );
  const hasCompletedSkincareConsultation = useAppSelector(
    userSelectors.getHasCompletedSkincareConsultation,
  );

  if (isStandalone) {
    return null;
  }

  const progressTrackerCategories = progressTrackerCategoriesList[consultationCategory];

  const currentlyShownQuestionCategoryIndex =
    question?.progressCategory &&
    progressTrackerCategories.findIndex(
      ({ code }: { code: string }) => code === question.progressCategory,
    );
  const currentlyShownQuestionCategoryQuestions =
    question?.progressCategory &&
    questions.filter((q) => q?.progressCategory === question.progressCategory);
  const indexOfCurrentlyShownQuestionInCategory: number | '' | undefined =
    question?.name && findIndex({ name: question.name }, currentlyShownQuestionCategoryQuestions);

  return (
    <ProgressTrackerContainer>
      {progressTrackerCategories?.map((renderedCategory, renderedCategoryIndex) => {
        // Get rendered category status (not started, in progress or completed) based on the index of the category for the question shown on the screen vs the index of the category we are rendering in this component
        const isRenderedCategoryCompleted =
          currentlyShownQuestionCategoryIndex &&
          currentlyShownQuestionCategoryIndex > renderedCategoryIndex;
        const isRenderedCategoryCurrent =
          currentlyShownQuestionCategoryIndex === renderedCategoryIndex;

        // We let users click on a category to return to the 1st question of it if they have already passed it or have a completed hair profile
        const isInteractive =
          isRenderedCategoryCompleted ||
          isRenderedCategoryCurrent ||
          (consultationCategory === productsCategories.HAIRCARE &&
            hasCompletedHaircareConsultation) ||
          (consultationCategory === productsCategories.SKINCARE &&
            hasCompletedSkincareConsultation);

        // Compute width of bar advancement
        let advancementCompletedWidth = '0';
        if (isRenderedCategoryCurrent) {
          // If the first screen of the category is a TransitionScreen we want the progress tracker to start at 0 otherwise we want it to be already advanced
          const categoryStartsWithTransitionScreen =
            currentlyShownQuestionCategoryQuestions &&
            currentlyShownQuestionCategoryQuestions[0]?.Component === TransitionComponent;
          // Transition screen if first – we compute bar width [0, n[
          if (
            categoryStartsWithTransitionScreen &&
            (indexOfCurrentlyShownQuestionInCategory !== null ||
              indexOfCurrentlyShownQuestionInCategory !== undefined)
          ) {
            advancementCompletedWidth = `${
              (Number(indexOfCurrentlyShownQuestionInCategory) /
                (currentlyShownQuestionCategoryQuestions?.length - 1)) *
              100
            }%`;
          }
          // First screen is not a transition – we compute bar width ]0, n]
          else if (
            indexOfCurrentlyShownQuestionInCategory !== null ||
            indexOfCurrentlyShownQuestionInCategory !== undefined
          ) {
            advancementCompletedWidth =
              (currentlyShownQuestionCategoryQuestions &&
                `${
                  ((Number(indexOfCurrentlyShownQuestionInCategory) + 1) /
                    currentlyShownQuestionCategoryQuestions.length) *
                  100
                }%`) ??
              '0';
          }
        } else if (isRenderedCategoryCompleted) {
          advancementCompletedWidth = '100%';
        }

        const handleAction = () => {
          if (!isInteractive) {
            return null;
          }
          const foundQuestion = questions?.find(
            (q) =>
              q?.progressCategory === renderedCategory?.code &&
              q.shouldBeIncluded({
                isAuthenticated,
              }),
          );

          if (!foundQuestion?.route) {
            return Sentry.captureMessage(
              `[Progress Tracker] Cannot redirect with renderedCategory ${renderedCategory?.code}. Matching question not found.`,
            );
          }

          // Redirect to the 1st route of the clicked progressCategory
          return navigate(foundQuestion.route);
        };

        // We have a specific (shorter) label for at least one category
        const label =
          isMobile && renderedCategory.labelMobile
            ? renderedCategory.labelMobile
            : renderedCategory.label;

        return (
          <CategoryButton
            key={renderedCategory.code}
            aria-label={`${isInteractive ? 'Navigate to ' : ''}${renderedCategory.label}`}
            data-click={renderedCategory.code}
            data-from="consultation-progress-bar"
            data-testid={`consultation-progress-${renderedCategory.code}`}
            disabled={!isInteractive}
            isClickable={isInteractive}
            onClick={handleAction}
            /* WARN: This prop is needed to prevent the button from becoming part of a parent form */
            type="button"
          >
            <Advancement style={{ width: advancementCompletedWidth }} />
            <Text
              align="center"
              color={
                isRenderedCategoryCompleted
                  ? 'neutral.800'
                  : isRenderedCategoryCurrent
                  ? 'neutral.900'
                  : 'neutral.700'
              }
              position="relative"
              variant="label3medium"
            >
              {label}
            </Text>
          </CategoryButton>
        );
      })}
    </ProgressTrackerContainer>
  );
};
